import { useEffect, useState } from "react";
import { useInView, useReducedMotion } from "@/lib/motion";
import { LeafScene, LessonChrome, PhoneFrame } from "./Phone";
import { DayHeading, DaySection } from "./day";

/** Scripted: "Ask a question" turns into a chat pinned to the beat, and the answer explains the why. */
const BEAT = "It pulls in carbon dioxide from the air through tiny pores in its leaves.";
const QUESTION = "Why tiny pores? Why not take it in through the whole leaf?";
const ANSWER =
  "Because the leaf has to hold on to water. Its waxy skin keeps water in, and that also keeps gas out. The pores (stomata) are doors it can open for carbon dioxide and shut when the air is dry, so it trades a little water for a lot of carbon.";

// Timeline, ms after the phone comes into view.
const STEPS = [900, 1500, 1500 + QUESTION.length * 32 + 300, 0, 0] as const;

export function Tutor() {
  const [box, seen] = useInView<HTMLDivElement>("-25% 0px");
  const reduced = useReducedMotion();
  const [run, setRun] = useState(0);
  const [t, setT] = useState(0); // elapsed ms of the script

  useEffect(() => {
    if (!seen) return;
    if (reduced) {
      setT(99999);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const tick = () => {
      const e = performance.now() - t0;
      setT(e);
      if (e < 9000) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, run, reduced]);

  const askAt = STEPS[0];
  const sheetAt = STEPS[1];
  const sentAt = STEPS[2];
  const answerAt = sentAt + 1500;
  const typed = t < sheetAt ? "" : QUESTION.slice(0, Math.floor((t - sheetAt - 250) / 32));
  const sent = t >= sentAt;
  const thinking = sent && t < answerAt;
  const answered = t >= answerAt;

  return (
    <DaySection id="tutor" label="The tutor">
      <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div className="lg:order-2">
          <DayHeading
            eyebrow="Ask anything, any time"
            title="Stuck? Ask right there, mid-lesson."
            intro="Your tutor knows what’s on screen, what you’ve learned so far and what you’re working toward, so the answer fits you. And it explains why, not just what."
          />
          <p className="mt-5 text-[15px] leading-relaxed text-[#6B6780]">
            Big question? Turn it into a side trip, and your main course waits right where you left it.
          </p>
          {!reduced && (
            <button type="button" onClick={() => setRun(r => r + 1)} className="mt-6 text-sm font-extrabold text-[#6B4EFF] hover:text-[#4128B8]">
              ↻ Play the demo again
            </button>
          )}
        </div>
        <div ref={box} className="lg:order-1">
          <PhoneFrame>
            <LeafScene beat={1} />
            <LessonChrome beats={4} at={1} narration={BEAT} askPressed={t >= askAt} />
            {/* The chat sheet, pinned to the beat. */}
            <div
              className="absolute inset-x-0 bottom-0 z-30 flex h-[72%] flex-col rounded-t-[26px] border-t border-white/15 bg-[rgba(16,12,48,0.97)] px-3.5 pt-3 pb-3 shadow-[0_-20px_60px_rgba(0,0,0,0.5)] backdrop-blur"
              style={{ transform: `translateY(${t >= sheetAt ? 0 : 105}%)`, transition: "transform 450ms cubic-bezier(.3,1,.4,1)" }}
              aria-hidden={t < sheetAt}
            >
              <div className="mx-auto h-1 w-9 rounded-full bg-white/25" />
              <div className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] px-2.5 py-2">
                <p className="text-[10px] font-black tracking-[0.1em] text-gold uppercase">Beat 2 of 4</p>
                <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug font-bold text-white/70">{BEAT}</p>
              </div>
              <div className="mt-3 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                {sent && <p className="la-fade-up ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#6B4EFF] px-3 py-2 text-[12.5px] leading-snug font-extrabold text-white">{QUESTION}</p>}
                {thinking && (
                  <div className="flex gap-1 px-2 py-2" aria-label="Thinking">
                    {[0, 1, 2].map(i => (
                      <span key={i} className="size-1.5 rounded-full bg-white/60 la-blink" style={{ animationDelay: `${i * 0.18}s` }} />
                    ))}
                  </div>
                )}
                {answered && (
                  <>
                    <p className="la-fade-up max-w-[92%] rounded-2xl rounded-bl-md bg-white/[0.08] px-3 py-2 text-[12.5px] leading-[1.42] font-bold text-white">{ANSWER}</p>
                    <div className="la-fade-up flex gap-1.5" style={{ animationDelay: "500ms" }}>
                      <span className="rounded-full border border-white/20 px-2.5 py-1 text-[11px] font-extrabold text-white/80">Back to the lesson</span>
                      <span className="rounded-full border border-gold/50 px-2.5 py-1 text-[11px] font-extrabold text-gold">+ Make it a side trip</span>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-2 flex h-10 items-center rounded-xl border-2 border-white/15 bg-white/[0.06] px-3 text-[12.5px] font-extrabold">
                {sent ? (
                  <span className="text-white/40">Ask a follow-up…</span>
                ) : (
                  <span className="truncate text-white">
                    {typed}
                    <span className="la-blink text-[#9B82FF]">|</span>
                  </span>
                )}
              </div>
            </div>
          </PhoneFrame>
          <p className="mx-auto mt-4 max-w-[300px] text-center text-xs font-bold text-[#6B6780]">A scripted demo of the in-lesson chat.</p>
        </div>
      </div>
    </DaySection>
  );
}
